import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Check, Package, Sparkles } from "lucide-react";
import { formatEuro } from "../context/CartContext";

/**
 * Pagina di conferma ordine — /checkout/conferma.
 *
 * Legge l'ultimo ordine da sessionStorage.
 * Non contiene dati sensibili: solo riepilogo e prossimi passi.
 */
export default function OrderConfirmedPage() {
  const order = useMemo(() => {
    try {
      const raw = window.sessionStorage.getItem("time-inside:last-order");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-20 sm:py-28 max-w-3xl mx-auto text-center">
      <div className="mx-auto w-16 h-16 rounded-full bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center mb-6 shadow-[0_10px_40px_-10px_rgba(16,185,129,0.5)]">
        <Check className="w-7 h-7 text-emerald-300" />
      </div>
      <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-400/80 mb-3">
        Ordine ricevuto
      </div>
      <h1
        data-testid="order-confirmed-title"
        className="text-4xl sm:text-5xl font-extralight tracking-tight text-white leading-[1.05]"
      >
        Grazie.
      </h1>
      <p className="mt-4 text-lg text-white/70 font-light max-w-xl mx-auto leading-relaxed">
        Il tuo ordine è stato registrato con successo. Il Tempo che dedicherai a TIME
        tornerà in valore, capitolo dopo capitolo.
      </p>

      {order ? (
        <div
          data-testid="order-summary"
          className="mt-10 mx-auto max-w-lg text-left rounded-2xl bg-white/[0.02] border border-white/5 p-6 sm:p-7"
        >
          <div className="flex items-center justify-between gap-3 text-[10px] tracking-[0.28em] uppercase text-white/40 font-mono">
            <span>Ordine</span>
            <span data-testid="order-id" className="text-emerald-300">
              {order.id}
            </span>
          </div>
          <ul className="mt-5 space-y-3">
            {order.items?.map((it) => (
              <li key={it.slug} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-white/85 font-light">{it.title}</div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 font-mono mt-0.5">
                    × {it.quantity}
                    {it.format ? ` · ${it.format}` : ""}
                  </div>
                </div>
                <div className="text-amber-400 font-mono tabular-nums shrink-0">
                  {formatEuro(it.price * it.quantity)}
                </div>
              </li>
            ))}
          </ul>
          <div className="my-5 h-px bg-white/10" />
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] tracking-[0.32em] uppercase text-white/50 font-mono">
              Totale
            </span>
            <span
              data-testid="order-total"
              className="text-2xl text-amber-400 font-light tabular-nums"
              style={{ textShadow: "0 0 18px rgba(212,175,55,0.3)" }}
            >
              {formatEuro(order.total)}
            </span>
          </div>
          <p className="mt-6 text-[12px] text-white/50 font-light leading-relaxed">
            Una copia di riepilogo è stata preparata per{" "}
            <span className="text-white/80">{order.customer?.email || "la tua email"}</span>.
          </p>
        </div>
      ) : (
        <p className="mt-8 text-white/50 font-light">
          Non è stato possibile recuperare il dettaglio dell'ordine. Puoi comunque tornare al catalogo.
        </p>
      )}

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          to="/prodotti"
          data-testid="order-continue-shopping"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/30 transition-colors text-[11px] tracking-[0.28em] uppercase font-mono"
        >
          <Package className="w-4 h-4" />
          Continua a esplorare
        </Link>
        <Link
          to="/"
          data-testid="order-back-home"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-400 text-black hover:bg-emerald-300 transition-colors text-[11px] tracking-[0.28em] uppercase font-medium"
        >
          <Sparkles className="w-4 h-4" />
          Torna alla Home
        </Link>
      </div>
    </div>
  );
}
